import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiFolder,
  FiGitBranch,
  FiPlus,
  FiRefreshCcw,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";

import AddEditCategory from "../Categories/AddEditCategory";


/* =========================================================
   HELPERS
========================================================= */

const getCategoryId = (
  value
) => {
  if (!value) {
    return "";
  }

  if (
    typeof value ===
    "object"
  ) {
    return String(
      value?._id ||
      ""
    );
  }

  return String(
    value
  );
};


const getParentId = (
  category
) => {
  if (
    !category
      ?.parentCategory
  ) {
    return "";
  }

  if (
    typeof category
      .parentCategory ===
    "object"
  ) {
    return String(
      category
        .parentCategory
        ?._id ||
      ""
    );
  }

  return String(
    category
      .parentCategory
  );
};


const getCategoryLevel = (
  category
) => {
  const level =
    Number(
      category?.level
    );

  return Number.isInteger(
    level
  )
    ? level
    : 0;
};


const getCategoryLabel = (
  category
) => {
  if (!category) {
    return "";
  }

  return (
    category?._treePath ||
    category?.path ||
    category?.name ||
    ""
  );
};


/* =========================================================
   COMPONENT
========================================================= */

const CategorySelect = ({
  category = "",

  subCategory = "",

  onCategoryChange,

  onSubCategoryChange,

  required = false,

  disabled = false,

  categoryError = "",

  subCategoryError = "",
}) => {
  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  /* =======================================================
     STATE
  ======================================================= */

  const [
    categories,
    setCategories,
  ] =
    useState([]);


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    loadError,
    setLoadError,
  ] =
    useState("");


  const [
    addCategoryOpen,
    setAddCategoryOpen,
  ] =
    useState(false);


  /*
   * null:
   * create root category
   *
   * category object:
   * create under selected category
   */

  const [
    parentForNewCategory,
    setParentForNewCategory,
  ] =
    useState(null);


  /* =======================================================
     NORMALIZED VALUES
  ======================================================= */

  const selectedCategoryId =
    getCategoryId(
      category
    );


  const selectedSubCategoryId =
    getCategoryId(
      subCategory
    );


  /* =======================================================
     LOAD CATEGORY DROPDOWN
  ======================================================= */

  const fetchCategories =
    useCallback(
      async () => {
        try {
          setLoading(
            true
          );

          setLoadError(
            ""
          );


          const response =
            await api.post(
              config
                .getInventoryCategoryDropdown,

              {
                search:
                  "",

                includeInactive:
                  false,
              }
            );


          if (
            response?.status !==
              "success" &&
            response?.success !==
              true
          ) {
            throw new Error(
              response?.message ||
              "Unable to load categories."
            );
          }


          const records =
            Array.isArray(
              response?.data
            )
              ? response.data
              : Array.isArray(
                    response
                      ?.data
                      ?.categories
                  )
                ? response
                    .data
                    .categories
                : [];


          setCategories(
            records
          );

          return records;

        } catch (
          error
        ) {
          console.error(
            "Category dropdown error:",
            error
          );


          setCategories(
            []
          );


          setLoadError(
            error?.response
              ?.data
              ?.message ||
            error?.message ||
            "Unable to load categories."
          );


          return [];

        } finally {
          setLoading(
            false
          );
        }
      },
      [
        api,
      ]
    );


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(
    () => {
      fetchCategories();
    },
    [
      fetchCategories,
    ]
  );


  /* =======================================================
     ROOT CATEGORIES
  ======================================================= */

  const rootCategories =
    useMemo(
      () =>
        categories.filter(
          (
            item
          ) =>
            !getParentId(
              item
            )
        ),
      [
        categories,
      ]
    );


  /* =======================================================
     SELECTED ROOT
  ======================================================= */

  const selectedRoot =
    useMemo(
      () =>
        categories.find(
          (
            item
          ) =>
            String(
              item?._id ||
              ""
            ) ===
            selectedCategoryId
        ) ||
        null,
      [
        categories,
        selectedCategoryId,
      ]
    );


  /* =======================================================
     DESCENDANTS

     We allow any nested level under the selected
     root category.
  ======================================================= */

  const descendantCategories =
    useMemo(
      () => {
        if (
          !selectedCategoryId
        ) {
          return [];
        }


        const result =
          [];


        const collectChildren =
          (
            parentId
          ) => {
            const children =
              categories.filter(
                (
                  item
                ) =>
                  getParentId(
                    item
                  ) ===
                  String(
                    parentId
                  )
              );


            children.forEach(
              (
                child
              ) => {
                result.push(
                  child
                );


                collectChildren(
                  child._id
                );
              }
            );
          };


        collectChildren(
          selectedCategoryId
        );


        return result;
      },
      [
        categories,
        selectedCategoryId,
      ]
    );


  /* =======================================================
     SELECTED CHILD
  ======================================================= */

  const selectedChild =
    useMemo(
      () =>
        categories.find(
          (
            item
          ) =>
            String(
              item?._id ||
              ""
            ) ===
            selectedSubCategoryId
        ) ||
        null,
      [
        categories,
        selectedSubCategoryId,
      ]
    );


  /* =======================================================
     CATEGORY CHANGE
  ======================================================= */

  const handleCategoryChange =
    (
      event
    ) => {
      const value =
        event.target
          .value;


      if (
        typeof onCategoryChange ===
        "function"
      ) {
        onCategoryChange(
          value
        );
      }


      /*
       * Changing main category makes any
       * previously selected nested category invalid.
       */

      if (
        typeof onSubCategoryChange ===
        "function"
      ) {
        onSubCategoryChange(
          ""
        );
      }
    };


  /* =======================================================
     SUBCATEGORY CHANGE
  ======================================================= */

  const handleSubCategoryChange =
    (
      event
    ) => {
      if (
        typeof onSubCategoryChange ===
        "function"
      ) {
        onSubCategoryChange(
          event.target
            .value
        );
      }
    };


  /* =======================================================
     ADD ROOT CATEGORY
  ======================================================= */

  const handleAddRootCategory =
    () => {
      setParentForNewCategory(
        null
      );

      setAddCategoryOpen(
        true
      );
    };


  /* =======================================================
     ADD CHILD CATEGORY

     Priority:

     selected nested category
            ↓
     selected main category

     This lets the user continue nesting:

     Accessories
       ↓
     Keyboard
       ↓
     Mechanical
       ↓
     Wireless
  ======================================================= */

  const handleAddChildCategory =
    () => {
      const parent =
        selectedChild ||
        selectedRoot ||
        null;


      if (!parent) {
        return;
      }


      setParentForNewCategory(
        parent
      );


      setAddCategoryOpen(
        true
      );
    };


  /* =======================================================
     CATEGORY CREATED
  ======================================================= */

  const handleCategoryCreated =
    async (
      responseData
    ) => {
      /*
       * Controller responses may return:
       *
       * data
       * data.category
       *
       * Support both.
       */

      const createdCategory =
        responseData
          ?.category ||
        responseData ||
        null;


      setAddCategoryOpen(
        false
      );


      const latestCategories =
        await fetchCategories();


      if (
        !createdCategory
          ?._id
      ) {
        return;
      }


      const createdId =
        String(
          createdCategory
            ._id
        );


      const createdParentId =
        getParentId(
          createdCategory
        ) ||
        getCategoryId(
          parentForNewCategory
        );


      /*
       * ROOT CATEGORY CREATED
       */

      if (
        !createdParentId
      ) {
        if (
          typeof onCategoryChange ===
          "function"
        ) {
          onCategoryChange(
            createdId
          );
        }


        if (
          typeof onSubCategoryChange ===
          "function"
        ) {
          onSubCategoryChange(
            ""
          );
        }


        setParentForNewCategory(
          null
        );

        return;
      }


      /*
       * CHILD / NESTED CATEGORY CREATED
       *
       * Find its root ancestor.
       */

      const findCategory =
        (
          id
        ) =>
          latestCategories.find(
            (
              item
            ) =>
              String(
                item?._id ||
                ""
              ) ===
              String(
                id ||
                ""
              )
          );


      let current =
        findCategory(
          createdId
        ) ||
        createdCategory;


      let root =
        current;


      let safety =
        0;


      while (
        getParentId(
          root
        ) &&
        safety <
          50
      ) {
        const parent =
          findCategory(
            getParentId(
              root
            )
          );


        if (!parent) {
          break;
        }


        root =
          parent;

        safety +=
          1;
      }


      if (
        typeof onCategoryChange ===
        "function"
      ) {
        onCategoryChange(
          String(
            root?._id ||
            selectedCategoryId ||
            ""
          )
        );
      }


      if (
        typeof onSubCategoryChange ===
        "function"
      ) {
        onSubCategoryChange(
          createdId
        );
      }


      setParentForNewCategory(
        null
      );
    };


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
      <div
        className="inventory-category-select"
      >
        {/* =================================================
            MAIN CATEGORY
        ================================================= */}

        <div
          className="inventory-category-select-field"
        >
          <div
            className="inventory-category-select-label-row"
          >
            <label>
              <FiFolder />

              Main Category

              {required ? (
                <span>
                  *
                </span>
              ) : null}
            </label>


            <button
              type="button"
              className="inventory-category-quick-add"
              onClick={
                handleAddRootCategory
              }
              disabled={
                disabled
              }
              title="Add new category"
            >
              <FiPlus />

              Add Category
            </button>
          </div>


          <select
            value={
              selectedCategoryId
            }
            onChange={
              handleCategoryChange
            }
            disabled={
              disabled ||
              loading
            }
          >
            <option
              value=""
            >
              {loading
                ? "Loading categories..."
                : "Select main category"}
            </option>


            {rootCategories.map(
              (
                item
              ) => (
                <option
                  key={
                    item._id
                  }
                  value={
                    item._id
                  }
                >
                  {item.name}
                </option>
              )
            )}
          </select>


          {categoryError ? (
            <small
              className="inventory-category-select-error"
            >
              {categoryError}
            </small>
          ) : null}
        </div>


        {/* =================================================
            CHILD / NESTED CATEGORY
        ================================================= */}

        <div
          className="inventory-category-select-field"
        >
          <div
            className="inventory-category-select-label-row"
          >
            <label>
              <FiGitBranch />

              Sub / Child Category
            </label>


            <button
              type="button"
              className="inventory-category-quick-add inventory-category-quick-add-child"
              onClick={
                handleAddChildCategory
              }
              disabled={
                disabled ||
                !selectedCategoryId
              }
              title={
                selectedSubCategoryId
                  ? "Add child under selected subcategory"
                  : "Add child under selected main category"
              }
            >
              <FiPlus />

              {selectedSubCategoryId
                ? "Add Nested"
                : "Add Child"}
            </button>
          </div>


          <select
            value={
              selectedSubCategoryId
            }
            onChange={
              handleSubCategoryChange
            }
            disabled={
              disabled ||
              loading ||
              !selectedCategoryId
            }
          >
            <option
              value=""
            >
              {!selectedCategoryId
                ? "Select main category first"
                : descendantCategories.length ===
                    0
                  ? "No child categories"
                  : "Select child / nested category"}
            </option>


            {descendantCategories.map(
              (
                item
              ) => {
                const level =
                  Math.max(
                    getCategoryLevel(
                      item
                    ) -
                      1,
                    0
                  );


                return (
                  <option
                    key={
                      item._id
                    }
                    value={
                      item._id
                    }
                  >
                    {`${"— ".repeat(
                      level
                    )}${getCategoryLabel(
                      item
                    )}`}
                  </option>
                );
              }
            )}
          </select>


          {subCategoryError ? (
            <small
              className="inventory-category-select-error"
            >
              {subCategoryError}
            </small>
          ) : (
            <span
              className="inventory-category-select-help"
            >
              Optional. Select any nested level
              below the main category.
            </span>
          )}
        </div>


        {/* =================================================
            RELOAD
        ================================================= */}

        <button
          type="button"
          className="inventory-category-select-refresh"
          onClick={
            fetchCategories
          }
          disabled={
            disabled ||
            loading
          }
          title="Reload categories"
          aria-label="Reload categories"
        >
          <FiRefreshCcw
            className={
              loading
                ? "spinning"
                : ""
            }
          />
        </button>


        {/* =================================================
            ERROR
        ================================================= */}

        {loadError ? (
          <div
            className="inventory-category-select-load-error"
          >
            {loadError}
          </div>
        ) : null}
      </div>


      {/* ===================================================
          REUSE EXISTING CATEGORY POPUP
      =================================================== */}

      <AddEditCategory
        open={
          addCategoryOpen
        }

        category={
          null
        }

        parentCategory={
          parentForNewCategory
        }

        onClose={() => {
          setAddCategoryOpen(
            false
          );

          setParentForNewCategory(
            null
          );
        }}

        onSuccess={
          handleCategoryCreated
        }
      />
    </>
  );
};


export default CategorySelect;